import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Users, Share2, Sparkles, ArrowRight,
  Instagram, Facebook, MessageCircle, Heart,
} from "lucide-react";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import { SOCIAL } from "../lib/social";
import { api } from "../lib/api";

/**
 * /tag — Landing page that mirrors the "TAG YOUR CAMPING MATES · LET'S
 * FILL THE MAP TOGETHER" Facebook post (160 likes / 23 shares / 2.5K
 * views — highest-performing creative as of June 2026).
 *
 * The post sends viewers to the homepage by default, which feels generic
 * after the high-energy hook. This page picks up the same energy:
 *  - Same black + orange + giant uppercase aesthetic
 *  - Live campsite count for credibility
 *  - One-tap "share with a mate" prefilled with the same caption
 *  - Three clear next-step CTAs (browse / community / follow)
 *
 * UTM-tagged sub-CTAs so we can track which platform refers most.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

function withUtm(path, source) {
  const u = new URL(`${PUBLIC_URL}${path}`);
  u.searchParams.set("utm_source", source);
  u.searchParams.set("utm_medium", "tag-landing");
  u.searchParams.set("utm_campaign", "fill-the-map-2026");
  return u.toString();
}

function tagShareUrl(channel) {
  const text = encodeURIComponent(
    "TAG YOUR CAMPING MATES 🏕️ — let's fill the map together. 22,160 UK campsites in one honest app, no booking-fee middleman. Try it: " + PUBLIC_URL + "/tag?utm_source=" + channel + "&utm_medium=tag-share"
  );
  const u = encodeURIComponent(PUBLIC_URL + "/tag?utm_source=" + channel + "&utm_medium=tag-share");
  switch (channel) {
    case "whatsapp": return `https://wa.me/?text=${text}`;
    case "facebook": return `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${text}`;
    case "x":        return `https://twitter.com/intent/tweet?text=${text}`;
    case "messenger":return `fb-messenger://share/?link=${u}`;
    case "telegram": return `https://t.me/share/url?url=${u}&text=${text}`;
    default:         return PUBLIC_URL;
  }
}

export default function TagYourMates() {
  const [count, setCount] = useState(22160);

  useEffect(() => {
    api.get("/campsites/stats").then((r) => {
      if (typeof r.data?.total === "number") setCount(r.data.total);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white" data-testid="tag-landing">
      <Header active="" />

      {/* HERO — mirrors the winning post creative */}
      <section className="relative overflow-hidden bg-[#0e0e0e]">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,69,0,0.4), transparent 40%), radial-gradient(circle at 80% 80%, rgba(251,191,36,0.3), transparent 40%)",
        }} aria-hidden="true" />
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#ff4500] text-white mono uppercase tracking-[0.22em] text-[10px] font-bold mb-6">
            <Sparkles className="w-3 h-3" /> Welcome from the Tag-A-Mate post
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight uppercase">
            Tag your<br />
            <span className="text-[#ff4500]">camping mates.</span><br />
            <span className="text-[#fbbf24]">Fill the map.</span>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-white/85 max-w-xl mx-auto leading-snug">
            <span className="font-display text-2xl text-[#fbbf24]">{count.toLocaleString()}</span> UK campsites · real prices · honest reviews · no booking-fee middleman.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/browse?utm_source=tag-landing&utm_medium=cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#ff4500] hover:bg-white hover:text-[#ff4500] transition-colors font-bold uppercase tracking-[0.18em] text-xs"
              data-testid="tag-cta-browse"
            >
              <MapPin className="w-4 h-4" /> Find your next pitch <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/register?utm_source=tag-landing&utm_medium=cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 border-2 border-white hover:bg-white hover:text-[#0e0e0e] transition-colors font-bold uppercase tracking-[0.18em] text-xs"
              data-testid="tag-cta-signup"
            >
              <Heart className="w-4 h-4" /> Free signup (20s)
            </Link>
          </div>
        </div>
      </section>

      {/* PASS IT ON · share row mirroring the original hook */}
      <section className="bg-[#fbbf24] text-[#0e0e0e] py-14 sm:py-20">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 text-center">
          <div className="mono uppercase tracking-[0.22em] text-[11px] font-bold mb-4">Step 2 · keep the chain going</div>
          <h2 className="font-display text-3xl sm:text-5xl leading-tight uppercase mb-3">
            Tag one camping mate.<br />
            <span className="underline decoration-[#ff4500] decoration-4 underline-offset-[6px]">That's it.</span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#1f1f1f]/80 max-w-md mx-auto">
            Every tag drops another pin on the UK camping map. Pick a channel, send in one tap, get back to your fire.
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-2 max-w-2xl mx-auto">
            {[
              { c: "whatsapp",  label: "WhatsApp",  bg: "#25D366", fg: "#fff", Icon: MessageCircle },
              { c: "facebook",  label: "Facebook",  bg: "#1877F2", fg: "#fff", Icon: Facebook },
              { c: "messenger", label: "Messenger", bg: "#0084FF", fg: "#fff", Icon: MessageCircle },
              { c: "telegram",  label: "Telegram",  bg: "#229ED9", fg: "#fff", Icon: Share2 },
              { c: "x",         label: "X / Twitter", bg: "#0e0e0e", fg: "#fff", Icon: Share2 },
            ].map(({ c, label, bg, fg, Icon }) => (
              <a
                key={c}
                href={tagShareUrl(c)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 py-3.5 px-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:opacity-90 transition-opacity"
                style={{ background: bg, color: fg }}
                data-testid={`tag-share-${c}`}
              >
                <Icon className="w-5 h-5" strokeWidth={2.2} />
                {label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET · matches the post's "follow for daily campsite drops" promise */}
      <section className="bg-[#0e0e0e] py-14 sm:py-20">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <div className="mono uppercase tracking-[0.22em] text-[#ff4500] text-[11px] font-bold text-center mb-3">What you get</div>
          <h2 className="font-display text-3xl sm:text-5xl text-center uppercase leading-tight mb-12">
            Follow us for daily<br />
            <span className="text-[#fbbf24]">campsite drops</span> <span className="text-white/60">·</span> <span className="text-[#ff4500]">honest tips</span>
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                Icon: MapPin, title: "Every UK pitch, one map", body: "22,160 sites — touring, glamping, wild, festival-adjacent. No booking-engine spin.",
                href: withUtm("/browse", "tag-landing"),
              },
              {
                Icon: Users, title: "The kindest UK community", body: "Real campers swapping real stories. Pets, families, solo — pitch in.",
                href: withUtm("/community", "tag-landing"),
              },
              {
                Icon: Heart, title: "14-day free Pro trial", body: "Try every Pro feature free for 14 days — walks, packing AI, partner discounts. Cancel anytime.",
                href: withUtm("/pro", "tag-landing"),
              },
            ].map(({ Icon, title, body, href }, i) => (
              <a
                key={i}
                href={href}
                className="block bg-[#1a1a1a] hover:bg-[#222] border border-white/10 hover:border-[#ff4500] transition-colors p-6 group"
                data-testid={`tag-feature-${i}`}
              >
                <Icon className="w-7 h-7 text-[#ff4500] mb-4" strokeWidth={1.8} />
                <div className="font-display text-xl mb-2 group-hover:text-[#fbbf24] transition-colors">{title}</div>
                <div className="text-sm text-white/70 leading-snug">{body}</div>
                <div className="mt-4 text-[10px] mono uppercase tracking-[0.22em] text-[#ff4500] font-bold inline-flex items-center gap-1">
                  Explore <ArrowRight className="w-3 h-3" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIALS — for people who came from the post but aren't followed yet */}
      <section className="bg-[#ff4500] text-white py-12 sm:py-16">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 text-center">
          <div className="mono uppercase tracking-[0.22em] text-[11px] font-bold mb-3">Follow the campfire</div>
          <div className="font-display text-3xl sm:text-4xl uppercase mb-6">Daily campsite drops · honest UK camping tips</div>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={SOCIAL.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#ff4500] hover:bg-[#0e0e0e] hover:text-white transition-colors font-bold uppercase tracking-[0.16em] text-xs"
              data-testid="tag-follow-fb"
            >
              <Facebook className="w-4 h-4" /> Follow on Facebook
            </a>
            <a
              href={SOCIAL.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#ff4500] hover:bg-[#0e0e0e] hover:text-white transition-colors font-bold uppercase tracking-[0.16em] text-xs"
              data-testid="tag-follow-ig"
            >
              <Instagram className="w-4 h-4" /> Follow on Instagram
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
